import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import { EscrowTransaction } from "../../../types/exchange";
import { exchangeService } from "../../../services/exchangeService";
import { useAuth } from "../../../contexts/AuthContext";

const ESCROW_STEPS = ["Pending", "Funded", "In Progress", "Completed"];

export const EscrowManager: React.FC = () => {
  const [escrows, setEscrows] = useState<EscrowTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [disputeDialogOpen, setDisputeDialogOpen] = useState(false);
  const [selectedEscrow, setSelectedEscrow] =
    useState<EscrowTransaction | null>(null);
  const [disputeReason, setDisputeReason] = useState("");
  const { user } = useAuth();

  useEffect(() => {
    const fetchEscrows = async () => {
      setLoading(true);
      try {
        const data = await exchangeService.getMyEscrows();
        setEscrows(data); // or setEscrows(data.escrows) if your backend returns { escrows: [...] }
        setError(null);
      } catch (err) {
        setError("Failed to load escrow transactions");
        setEscrows([]);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchEscrows();
    } else {
      setLoading(false);
      setEscrows([]);
    }
  }, [user]);

  const getStepIndex = (status: string): number => {
    switch (status) {
      case "pending":
        return 0;
      case "funded":
        return 1;
      case "in_progress":
        return 2;
      case "completed":
        return 3;
      default:
        return 0;
    }
  };

  const handleAction = async (
    escrow: EscrowTransaction,
    action: "fund" | "confirm" | "release"
  ) => {
    try {
      if (!user) {
        setError("Please log in to perform this action");
        return;
      }

      let updatedEscrow: EscrowTransaction;
      switch (action) {
        case "fund":
          updatedEscrow = await exchangeService.fundEscrow(escrow.id);
          break;
        case "confirm":
          updatedEscrow = await exchangeService.confirmDelivery(escrow.id);
          break;
        case "release":
          updatedEscrow = await exchangeService.releaseEscrow(escrow.id);
          break;
        default:
          return;
      }

      setEscrows(
        escrows.map((e) => (e.id === updatedEscrow.id ? updatedEscrow : e))
      );
      setError(null);
    } catch (err) {
      setError(`Failed to ${action} escrow`);
      console.error(`Error ${action}ing escrow:`, err);
    }
  };

  const handleDispute = async () => {
    if (!selectedEscrow || !disputeReason) return;

    try {
      const updatedEscrow = await exchangeService.disputeEscrow(
        selectedEscrow.id,
        { reason: disputeReason }
      );

      setEscrows(
        escrows.map((e) => (e.id === updatedEscrow.id ? updatedEscrow : e))
      );
      setDisputeDialogOpen(false);
      setDisputeReason("");
      setSelectedEscrow(null);
      setError(null);
    } catch (err) {
      setError("Failed to dispute escrow");
      console.error("Error disputing escrow:", err);
    }
  };

  if (loading) {
    return <Typography>Loading escrows...</Typography>;
  }

  return (
    <Box sx={{ p: 3 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Typography variant="h4" sx={{ mb: 3 }}>
        My Escrow Transactions
      </Typography>

      {escrows.length === 0 ? (
        <Alert severity="info">
          You don't have any escrow transactions yet.
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {escrows.map((escrow) => (
            <Grid item xs={12} key={escrow.id}>
              <Card>
                <CardContent>
                  <Box sx={{ mb: 2 }}>
                    <Stepper activeStep={getStepIndex(escrow.status)}>
                      {ESCROW_STEPS.map((label) => (
                        <Step key={label}>
                          <StepLabel>{label}</StepLabel>
                        </Step>
                      ))}
                    </Stepper>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="h6">Transaction Details</Typography>
                      <Typography>
                        Amount: {escrow.amount_usd} USD / {escrow.amount_bs} Bs
                      </Typography>
                      <Typography>
                        Rate: {escrow.rate_bs_per_usd} Bs/USD
                      </Typography>
                      <Typography>
                        Commission: {escrow.commission} Bs
                      </Typography>
                      <Typography>
                        Payment Method: {escrow.payment_method}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography variant="h6">
                        Status: {escrow.status}
                      </Typography>
                      {escrow.dispute_reason && (
                        <Alert severity="error" sx={{ mt: 1 }}>
                          Disputed: {escrow.dispute_reason}
                        </Alert>
                      )}

                      <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
                        {escrow.status === "pending" && (
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={() => handleAction(escrow, "fund")}
                          >
                            Fund Escrow
                          </Button>
                        )}

                        {escrow.status === "funded" && (
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={() => handleAction(escrow, "confirm")}
                          >
                            Confirm Delivery
                          </Button>
                        )}

                        {escrow.status === "in_progress" && (
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={() => handleAction(escrow, "release")}
                          >
                            Release Funds
                          </Button>
                        )}

                        {["pending", "funded", "in_progress"].includes(
                          escrow.status
                        ) && (
                          <Button
                            variant="outlined"
                            color="error"
                            onClick={() => {
                              setSelectedEscrow(escrow);
                              setDisputeDialogOpen(true);
                            }}
                          >
                            Dispute
                          </Button>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog
        open={disputeDialogOpen}
        onClose={() => setDisputeDialogOpen(false)}
      >
        <DialogTitle>Dispute Transaction</DialogTitle>
        <DialogContent>
          <TextField
            label="Reason for Dispute"
            multiline
            rows={4}
            value={disputeReason}
            onChange={(e) => setDisputeReason(e.target.value)}
            fullWidth
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisputeDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDispute}
            variant="contained"
            color="error"
            disabled={!disputeReason}
          >
            Submit Dispute
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
